import { FrostFreeStatus, HeatingNetworkFrostFreeStatus } from "@eisox/api/core-api";

import { m } from "~/paraglide/messages";
import type { EnhancedGateway } from "~/types/gateway.type";

import type { PeriodsType, PeriodType } from "./schemas.helper";

/**
 * Checks if frost-free periods are allowed for a given list of gateways.
 * @param gateways - List of gateways to check.
 * @returns True if frost-free periods are allowed, false otherwise.
 */
export const frostFreePeriodsAllowed = (gateways: EnhancedGateway[]) => gateways.some(g => g.softwareVersion);

/** Heating networks additionally offer `auto`, which follows the rooms they serve. */
export type FrostFreeMode = FrostFreeStatus | HeatingNetworkFrostFreeStatus;

export function getFrostFreeModes(options: { periodsAllowed: boolean; allowAuto: true }): readonly FrostFreeMode[];
export function getFrostFreeModes(options: { periodsAllowed: boolean; allowAuto?: false }): readonly FrostFreeStatus[];
export function getFrostFreeModes({
  periodsAllowed,
  allowAuto = false,
}: {
  periodsAllowed: boolean;
  allowAuto?: boolean;
}): readonly FrostFreeMode[] {
  return [
    FrostFreeStatus.ENABLED,
    FrostFreeStatus.DISABLED,
    ...(periodsAllowed ? [FrostFreeStatus.PERIODS] : []),
    ...(allowAuto ? [HeatingNetworkFrostFreeStatus.AUTO] : []),
  ];
}

export const getDefaultMode = <T extends FrostFreeMode>(status: string | undefined, modes: readonly T[]): T =>
  status && (modes as readonly string[]).includes(status) ? (status as T) : (FrostFreeStatus.DISABLED as T);

export const getDefaultPeriods = (periods?: PeriodType[]): PeriodsType =>
  periods?.map(p => ({ begin: p.begin, end: p.end, added: false, removed: false })) ?? [];

export const filterPeriods = (periods: PeriodsType) =>
  periods.filter(p => !p.removed).map(p => ({ begin: p.begin, end: p.end }));

// Both enums share their string values, so the label is resolved on the value rather than the member.
export const getFrostFreeStatusLabel = (status: FrostFreeMode): string => {
  switch (status as string) {
    case FrostFreeStatus.ENABLED:
      return m.activation({ activation: "true" });
    case FrostFreeStatus.DISABLED:
      return m.activation({ activation: "false" });
    case FrostFreeStatus.PERIODS:
      return m.configured_by_periods();
    default:
      return m.frost_free_auto();
  }
};

const getPeriodErrorMessage = (itemError: unknown): string | undefined => {
  if (typeof itemError === "string") return itemError;
  if (itemError && typeof itemError === "object" && "message" in itemError) return String(itemError.message);
  return undefined;
};

/** Attaches the validation message of each period to the period itself, for the `Periods` editor. */
export const mapPeriodsWithErrors = (
  periods: PeriodsType,
  fieldErrors: unknown[] | undefined,
): (PeriodsType[number] & { error?: string })[] =>
  periods.map((period, index) => ({ ...period, error: getPeriodErrorMessage(fieldErrors?.[index]) }));
